import {DI_TOKENS} from '@/di/di.tokens';
import {LOAD_ITEMS_ASSETS_ERROR} from '@/services/ErrorService/ErrorService.constants';
import {Assets, Texture} from 'pixi.js';

import {injected} from 'brandi';

import {Envs} from '@/types';

const MAX_LOAD_BUNDLES_RETRIES = 3;

enum BundleId {
  Items = 'items-bundle',
}

interface ItemsBundle {
  item1: Texture;
  item2: Texture;
  item3: Texture;
  item4: Texture;
  item5: Texture;
  item6: Texture;
  item7: Texture;
  item8: Texture;
  item9: Texture;
  item10: Texture;
  item11: Texture;
  item12: Texture;
  item13: Texture;
}

export class AssetsLoader {
  private readonly itemsBundleConfig: Record<keyof ItemsBundle, string> = {
    item1: this.buildTexturePath('sunSmall-670-2024-11-21-10-30-15', 'png'),
    item2: this.buildTexturePath('walletSmall-680-2024-11-19-08-09-43', 'png'),
    item3: this.buildTexturePath('hatSmall-680-2024-10-28-09-48-58', 'png'),
    item4: this.buildTexturePath('planeSmall-680-2024-10-28-09-48-58', 'png'),
    item5: this.buildTexturePath('rocketSmall-680-2024-11-19-08-09-43', 'png'),
    item6: this.buildTexturePath('bearSmall-680-2024-11-28-12-41-54', 'png'),
    item7: this.buildTexturePath('phoneSmall-672-2024-11-28-12-41-53', 'png'),
    item8: this.buildTexturePath('surpriseSmall-681-2024-12-09-11-27-58', 'png'),
    item9: this.buildTexturePath('dubaiSmall-680-2024-11-28-12-38-07', 'png'),
    item10: this.buildTexturePath('IrisSmall-681-2024-12-09-11-07-18', 'png'),
    item11: this.buildTexturePath('mouseSmall-680-2024-11-19-08-09-43', 'png'),
    item12: this.buildTexturePath('maximumPurchaseDaySmall-670-2024-12-09-12-26-01', 'png'),
    item13: this.buildTexturePath('crabSmall-680-2024-10-28-09-54-22', 'png'),
  };

  public itemsBundle!: ItemsBundle;
  private retryAttempts: number;

  constructor(private readonly envs: Envs) {
    Assets.addBundle(BundleId.Items, this.itemsBundleConfig);
    this.retryAttempts = 1;
  }

  public async loadBundle(): Promise<ItemsBundle | undefined> {
    return this.itemsBundle ? Promise.resolve(undefined) : this.loadItemsBundle();
  }

  private async loadItemsBundle(): Promise<ItemsBundle> {
    const bundle = await this.loadBundlesWithRetries<ItemsBundle>(BundleId.Items);
    this.itemsBundle = bundle;

    return bundle;
  }

  private async loadBundlesWithRetries<T>(bundleId: BundleId): Promise<T> {
    try {
      const assets = await Assets.loadBundle(bundleId);

      return assets;
    } catch (error: unknown) {
      this.retryAttempts++;

      if (this.retryAttempts === MAX_LOAD_BUNDLES_RETRIES) {
        this.retryAttempts = 1;
        throw new Error(LOAD_ITEMS_ASSETS_ERROR);
      }

      return this.loadBundlesWithRetries<T>(bundleId);
    }
  }

  private buildTexturePath(relativePath: string, ext: string): string {
    return `${this.getImagesBaseUrl()}/${relativePath}.${ext}`;
  }

  private getImagesBaseUrl(): string {
    return `${this.envs.APP_STATIC_BASE_URL}/static/ny25`;
  }
}

injected(AssetsLoader, DI_TOKENS.envs);
